import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Camera, 
  Upload, 
  FileText, 
  Package, 
  CheckCircle2, 
  AlertCircle,
  Loader2,
  ArrowLeft,
  Image as ImageIcon,
  Trash2,
  ClipboardSignature,
  History,
  Truck,
  Store,
  Edit2,
  Save,
  X,
  Search,
  Pencil,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  XCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIScanPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  
  // Get initial tab from URL or default to "scan"
  const initialTab = searchParams.get("tab") || "scan";
  
  // State for scanning
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [scannedFile, setScannedFile] = useState(null);
  
  // State for scanned data (editable)
  const [scannedData, setScannedData] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // State for saving
  const [isSaving, setIsSaving] = useState(false);
  
  // State for history with pagination
  const [podHistory, setPodHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [deleteConfirmPod, setDeleteConfirmPod] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [matchFilter, setMatchFilter] = useState("");
  const PAGE_SIZE = 25;
  
  // State for editing POD in history
  const [editingPod, setEditingPod] = useState(null);
  const [editingPodProducts, setEditingPodProducts] = useState([]);
  const [editingOrderNumber, setEditingOrderNumber] = useState("");
  const [editingCustomerName, setEditingCustomerName] = useState("");
  const [editingFreight, setEditingFreight] = useState(0);
  const [savingPodEdit, setSavingPodEdit] = useState(false);
  const [searchingProduct, setSearchingProduct] = useState(null);

  // Load history when tab changes or filter changes
  useEffect(() => {
    if (activeTab === "history") {
      loadPodHistory(1);
    }
  }, [activeTab, matchFilter]);

  // Cleanup blob URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  // Compress image before upload
  const compressImage = (file, maxWidth = 1600, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        let { width, height } = img;
        
        // Only resize if larger than maxWidth
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              reject(new Error('Kunde inte komprimera bilden'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Kunde inte läsa bilden'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle file selection and auto-analyze
  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Ogiltigt filformat. Endast JPEG, PNG och WEBP stöds.");
      return;
    }
    
    // Check file size (max 20MB)
    const MAX_SIZE = 20 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error("Filen är för stor. Max 20MB tillåtet.");
      return;
    }
    
    try {
      // Compress image if it's larger than 2MB
      let processedFile = file;
      if (file.size > 2 * 1024 * 1024) {
        toast.info("Komprimerar bilden...");
        processedFile = await compressImage(file);
      }
      
      // Store file and create preview using blob URL (more memory efficient)
      setScannedFile(processedFile);
      const blobUrl = URL.createObjectURL(processedFile);
      
      // Revoke old preview URL if exists
      if (previewImage && previewImage.startsWith('blob:')) {
        URL.revokeObjectURL(previewImage);
      }
      setPreviewImage(blobUrl);
      
      // Auto-analyze
      await analyzeDocument(processedFile);
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Kunde inte bearbeta filen: " + error.message);
    }
  }, [previewImage]);

  // Analyze document with AI
  const analyzeDocument = async (file) => {
    setIsAnalyzing(true);
    setScannedData(null);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", "delivery_note");
      
      const response = await axios.post(`${API_URL}/api/ai-scan/analyze-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
      });
      
      const result = response.data;
      
      // Transform result to editable format
      const products = (result.products || []).map((p, idx) => ({
        id: idx,
        ean: p.ean || "",
        artikelnummer: p.artikelnummer || "",
        produktnamn: p.produktnamn || "Okänd produkt",
        quantity: p.quantity || 1,
        price: p.price || 0,
        pricePerUnit: p.price ? (p.price / (p.quantity || 1)) : 0
      }));
      
      setScannedData({
        orderNumber: result.order_number || "",
        customerName: result.customer_name || "",
        deliveryDate: result.delivery_date || new Date().toISOString().split('T')[0],
        products: products,
        freight: result.freight || 0,
        totalAmount: result.total_amount || products.reduce((sum, p) => sum + (p.price || 0), 0),
        confidence: result.confidence || 0.5
      });
      
      toast.success(`Följesedel analyserad! ${products.length} produkt(er) identifierade.`);
      
    } catch (error) {
      console.error("Error analyzing document:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - analysen tog för lång tid. Försök med en mindre bild.");
      } else {
        const errorMessage = error.response?.data?.detail || error.message || "Kunde inte analysera dokumentet";
        toast.error(errorMessage);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Update product in scanned data
  const updateProduct = (productId, field, value) => {
    setScannedData(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (p.id === productId) {
          const updated = { ...p, [field]: value };
          // Recalculate price per unit if quantity or price changes
          if (field === "quantity" || field === "price") {
            updated.pricePerUnit = updated.price / (updated.quantity || 1);
          }
          return updated;
        }
        return p;
      })
    }));
  };

  // Remove product from list
  const removeProduct = (productId) => {
    setScannedData(prev => ({
      ...prev,
      products: prev.products.filter(p => p.id !== productId)
    }));
  };

  // Add new product to scanned data
  const addProduct = () => {
    setScannedData(prev => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: Date.now(), // Unique ID based on timestamp
          ean: "",
          artikelnummer: "",
          produktnamn: "Ny produkt",
          quantity: 1,
          price: 0,
          pricePerUnit: 0
        }
      ]
    }));
  };

  // Remove product from editing POD list
  const removeEditingProduct = (index) => {
    setEditingPodProducts(prev => prev.filter((_, i) => i !== index));
  };

  // Add new product to editing POD list
  const addEditingProduct = () => {
    setEditingPodProducts(prev => [
      ...prev,
      {
        ean: "",
        artikelnummer: "",
        produktnamn: "",
        productName: "",
        quantity: 1,
        salePrice: 0,
        matched: false
      }
    ]);
  };

  // Calculate total from products
  const calculateTotal = () => {
    if (!scannedData) return 0;
    return scannedData.products.reduce((sum, p) => sum + (p.price || 0), 0) + (scannedData.freight || 0);
  };

  // Save POD - matches against existing scan-outs
  const handleSavePod = async () => {
    if (!scannedData?.orderNumber?.trim()) {
      toast.error("Ordernummer saknas. Ange ordernummer för att fortsätta.");
      return;
    }
    
    if (scannedData.products.length === 0) {
      toast.error("Inga produkter att spara.");
      return;
    }
    
    setIsSaving(true);
    
    try {
      const formData = new FormData();
      formData.append("order_number", scannedData.orderNumber.trim());
      formData.append("customer_name", scannedData.customerName || "");
      formData.append("freight", scannedData.freight || 0);
      formData.append("products_json", JSON.stringify(scannedData.products));
      formData.append("total_amount", calculateTotal());
      
      if (scannedFile) {
        formData.append("document_file", scannedFile);
      }
      
      const response = await axios.post(`${API_URL}/api/ai-scan/save-delivery-proof`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const result = response.data;
      
      // Show result based on matching
      if (result.matched_deliveries > 0) {
        toast.success(
          `Leveransbevis sparat! Matchade ${result.matched_deliveries} tidigare utskanning(ar). ` +
          `Försäljningspris ${calculateTotal().toFixed(2)} kr registrerat.`
        );
      } else {
        toast.warning(
          "Leveransbevis sparat, men ingen tidigare utskanning hittades med detta ordernummer. " +
          "Kontrollera att produkterna har skannats ut först."
        );
      }
      
      // Reset and go to history
      setScannedData(null);
      setPreviewImage(null);
      setScannedFile(null);
      setActiveTab("history");
      setCurrentPage(1);
      loadPodHistory(1);
      
    } catch (error) {
      console.error("Error saving POD:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Kunde inte spara leveransbeviset";
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Load POD history with pagination
  const loadPodHistory = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (matchFilter) params.match_status = matchFilter;
      const response = await axios.get(`${API_URL}/api/ai-scan/proof-of-delivery`, { params });
      setPodHistory(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalItems(response.data.total || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error loading POD history:", error);
      toast.error("Kunde inte ladda historiken");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadPodHistory(newPage);
    }
  };

  // Delete POD
  const deletePod = async (pod) => {
    try {
      await axios.delete(`${API_URL}/api/ai-scan/proof-of-delivery/${pod.id}`);
      toast.success("Leveransbevis borttaget");
      // Reload current page, or go back if this was the last item on the page
      const newPage = podHistory.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      loadPodHistory(newPage);
    } catch (error) {
      toast.error("Fel vid borttagning");
    }
    setDeleteConfirmPod(null);
  };

  // View document - with timeout and cleanup
  const viewDocument = async (pod) => {
    if (!pod.has_document) {
      toast.error("Inget dokument finns för detta leveransbevis");
      return;
    }
    
    setLoadingDocument(true);
    try {
      const response = await axios.get(`${API_URL}/api/ai-scan/proof-of-delivery/document/${pod.id}`, {
        timeout: 30000 // 30 second timeout
      });
      setViewingDocument({
        id: pod.id,
        order_number: pod.order_number,
        customer_name: pod.customer_name,
        document_image: response.data.document_image
      });
    } catch (error) {
      console.error("Error loading document:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Timeout - dokumentet tog för lång tid att ladda");
      } else {
        toast.error("Kunde inte ladda dokumentet");
      }
    } finally {
      setLoadingDocument(false);
    }
  };
  
  // Cleanup when closing document viewer
  const closeDocumentViewer = useCallback(() => {
    setViewingDocument(null);
  }, []);

  // Edit POD - open edit dialog with all data
  const openEditPod = (pod) => {
    setEditingPod(pod);
    setEditingOrderNumber(pod.order_number || "");
    setEditingCustomerName(pod.customer_name || "");
    setEditingFreight(pod.freight || 0);
    // Map products with correct price field - use price, salePrice, or pricePerUnit
    setEditingPodProducts(pod.products?.map(p => ({ 
      ...p,
      // Ensure salePrice is set from any available price field
      salePrice: p.salePrice ?? p.price ?? p.pricePerUnit ?? 0
    })) || []);
  };

  // Search for product match
  const searchProductMatch = async (index, searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      toast.error("Ange artikelnummer eller EAN att söka efter");
      return;
    }
    
    const value = searchValue.trim();
    setSearchingProduct(index);
    
    try {
      const response = await axios.get(`${API_URL}/api/inventory`, {
        params: { search: value, limit: 10 }
      });
      const matches = response.data?.items || [];
      
      // Find exact or partial match
      let match = matches.find(m => m.ean === value || m.artikelnummer === value);
      if (!match && matches.length > 0) {
        match = matches.find(m => 
          (m.artikelnummer && m.artikelnummer.includes(value)) ||
          (m.ean && m.ean.includes(value))
        );
      }
      
      if (match) {
        setEditingPodProducts(prev => prev.map((p, i) => {
          if (i === index) {
            return {
              ...p,
              ean: match.ean,
              artikelnummer: match.artikelnummer,
              productName: match.produktnamn,
              matched: true
            };
          }
          return p;
        }));
        toast.success(`Matchad: ${match.artikelnummer}`);
      } else {
        toast.warning(`Ingen träff för "${value}"`);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Sökning misslyckades");
    } finally {
      setSearchingProduct(null);
    }
  };

  // Update product in edit mode
  const updateEditingProduct = (index, field, value) => {
    setEditingPodProducts(prev => prev.map((p, i) => {
      if (i === index) {
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  // Save edited POD with order number
  const saveEditedPod = async () => {
    if (!editingPod) return;
    
    setSavingPodEdit(true);
    try {
      const productsTotal = editingPodProducts.reduce((sum, p) => sum + (p.salePrice || 0) * (p.quantity || 1), 0);
      const totalWithFreight = productsTotal + (editingFreight || 0);
      
      const response = await axios.put(`${API_URL}/api/ai-scan/proof-of-delivery/${editingPod.id}`, {
        products: editingPodProducts,
        order_number: editingOrderNumber,
        customer_name: editingCustomerName,
        freight: editingFreight || 0,
        total_amount: totalWithFreight
      });
      
      const matchedCount = response.data.matched_deliveries || 0;
      if (matchedCount > 0) {
        toast.success(`Leveransbevis uppdaterat! Matchade ${matchedCount} leverans(er).`);
      } else {
        toast.success("Leveransbevis uppdaterat");
      }
      
      setEditingPod(null);
      setEditingPodProducts([]);
      setEditingOrderNumber("");
      setEditingCustomerName("");
      setEditingFreight(0);
      loadPodHistory(currentPage);
    } catch (error) {
      console.error("Error saving POD:", error);
      toast.error("Kunde inte spara ändringar");
    } finally {
      setSavingPodEdit(false);
    }
  };

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleString("sv-SE", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Reset scan
  const resetScan = () => {
    setScannedData(null);
    setPreviewImage(null);
    setScannedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-4">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight" data-testid="ai-scan-title">
              Följesedel
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Skanna följesedel för att registrera försäljningspris
          </p>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Main tabs */}
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          setSearchParams({ tab });
        }}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="scan" className="gap-2" data-testid="tab-scan">
              <Camera className="h-4 w-4" />
              Skanna
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
              <History className="h-4 w-4" />
              Historik
            </TabsTrigger>
          </TabsList>
          
          {/* SCAN TAB */}
          <TabsContent value="scan" className="space-y-6">
            {!scannedData ? (
              /* Step 1: Upload/Scan */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Ladda upp följesedel
                  </CardTitle>
                  <CardDescription>
                    AI:n identifierar automatiskt produkter, priser och frakt
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-area"
                  >
                    {isAnalyzing ? (
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="h-12 w-12 text-primary animate-spin" />
                        <p className="text-muted-foreground">Analyserar följesedel...</p>
                        <p className="text-xs text-muted-foreground">AI identifierar produkter och priser</p>
                      </div>
                    ) : previewImage ? (
                      <div className="flex flex-col items-center gap-4">
                        <img 
                          src={previewImage} 
                          alt="Preview" 
                          className="max-h-64 rounded-lg shadow-lg"
                        />
                        <Button variant="outline" size="sm">
                          <Camera className="h-4 w-4 mr-2" />
                          Byt bild
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <div className="p-4 bg-muted rounded-full">
                          <Upload className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Klicka för att ladda upp</p>
                          <p className="text-sm text-muted-foreground">
                            JPEG, PNG eller WEBP
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleFileSelect}
                    data-testid="file-input"
                  />
                </CardContent>
              </Card>
            ) : (
              /* Results: Show editable product list */
              <div className="space-y-4">
                {/* Header info */}
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-4">
                      {previewImage && (
                        <img 
                          src={previewImage} 
                          alt="Följesedel" 
                          className="w-24 h-24 object-cover rounded-lg border"
                        />
                      )}
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground">Ordernummer</Label>
                            <Input
                              value={scannedData.orderNumber}
                              onChange={(e) => setScannedData(prev => ({ ...prev, orderNumber: e.target.value }))}
                              placeholder="Ordernummer"
                              className="mt-1"
                              data-testid="order-number-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Kundnamn</Label>
                            <Input
                              value={scannedData.customerName}
                              onChange={(e) => setScannedData(prev => ({ ...prev, customerName: e.target.value }))}
                              placeholder="Kundnamn"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={scannedData.confidence > 0.7 ? "default" : "secondary"}>
                            AI-säkerhet: {Math.round(scannedData.confidence * 100)}%
                          </Badge>
                          <Badge variant="outline">
                            {scannedData.products.length} produkt(er)
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Products table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Produkter
                    </CardTitle>
                    <CardDescription>
                      Redigera om AI:n tolkat något fel
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produkt</TableHead>
                          <TableHead className="w-20 text-center">Antal</TableHead>
                          <TableHead className="w-28 text-right">Pris</TableHead>
                          <TableHead className="w-24 text-right">Per st</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scannedData.products.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{product.produktnamn}</p>
                                <p className="text-xs text-muted-foreground">
                                  {product.artikelnummer && `Art: ${product.artikelnummer}`}
                                  {product.artikelnummer && product.ean && " | "}
                                  {product.ean && `EAN: ${product.ean}`}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                value={product.quantity}
                                onChange={(e) => updateProduct(product.id, "quantity", parseInt(e.target.value) || 1)}
                                className="w-16 text-center"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={product.price}
                                onChange={(e) => updateProduct(product.id, "price", parseFloat(e.target.value) || 0)}
                                className="w-24 text-right"
                              />
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {product.pricePerUnit?.toFixed(2)} kr
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeProduct(product.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Add product button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addProduct}
                      className="w-full mt-3"
                      data-testid="add-product-btn"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Lägg till produkt
                    </Button>
                    
                    {/* Freight and Total */}
                    <Separator className="my-4" />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Frakt:</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={scannedData.freight}
                            onChange={(e) => setScannedData(prev => ({ ...prev, freight: parseFloat(e.target.value) || 0 }))}
                            className="w-28 text-right"
                          />
                          <span className="text-sm">kr</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-lg font-bold">
                        <span>Totalt:</span>
                        <span>{calculateTotal().toFixed(2)} kr</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={resetScan}
                    className="flex-1"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Avbryt
                  </Button>
                  <Button
                    onClick={handleSavePod}
                    disabled={isSaving || !scannedData.orderNumber.trim()}
                    className="flex-1"
                    data-testid="save-pod-btn"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sparar...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Skapa leveransbevis
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* HISTORY TAB */}
          <TabsContent value="history">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Leveransbevis
                </CardTitle>
                <div className="flex items-center gap-2">
                  {/* Match status filter */}
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant={matchFilter === "" ? "default" : "outline"} 
                      className="h-7 text-xs"
                      onClick={() => setMatchFilter("")}
                    >
                      Alla {!matchFilter && totalItems > 0 && `(${totalItems})`}
                    </Button>
                    <Button 
                      size="sm" 
                      variant={matchFilter === "MATCHED" ? "default" : "outline"} 
                      className={`h-7 text-xs ${matchFilter !== "MATCHED" ? "text-green-500 border-green-500/30" : "bg-green-500"}`}
                      onClick={() => setMatchFilter(matchFilter === "MATCHED" ? "" : "MATCHED")}
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Matchad
                    </Button>
                    <Button 
                      size="sm" 
                      variant={matchFilter === "PRODUCT_MISMATCH" ? "default" : "outline"} 
                      className={`h-7 text-xs ${matchFilter !== "PRODUCT_MISMATCH" ? "text-amber-500 border-amber-500/30" : "bg-amber-500"}`}
                      onClick={() => setMatchFilter(matchFilter === "PRODUCT_MISMATCH" ? "" : "PRODUCT_MISMATCH")}
                    >
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Avvikelse
                    </Button>
                    <Button 
                      size="sm" 
                      variant={matchFilter === "NO_ORDER_MATCH" ? "default" : "outline"} 
                      className={`h-7 text-xs ${matchFilter !== "NO_ORDER_MATCH" ? "text-red-500 border-red-500/30" : "bg-red-500"}`}
                      onClick={() => setMatchFilter(matchFilter === "NO_ORDER_MATCH" ? "" : "NO_ORDER_MATCH")}
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Ingen match
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : podHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardSignature className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Inga leveransbevis sparade</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {podHistory.map((pod) => (
                      <div 
                        key={pod.id}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                        data-testid={`pod-item-${pod.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`p-2 rounded-full ${pod.delivery_type === 'route' ? 'bg-blue-500/10' : 'bg-green-500/10'}`}>
                            {pod.delivery_type === 'route' ? (
                              <Truck className="h-4 w-4 text-blue-500" />
                            ) : (
                              <Store className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-mono font-bold">{pod.order_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {pod.customer_name || "Okänd kund"} • {formatDate(pod.created_at)}
                              {pod.created_by && (
                                <span className="ml-2 text-primary">• Uppladdad av: {pod.created_by}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          {pod.total_amount && (
                            <Badge variant="outline" className="text-xs">
                              {pod.total_amount.toFixed(2)} kr
                            </Badge>
                          )}
                          {/* Match status indicator */}
                          {pod.match_status === "MATCHED" ? (
                            <Badge className="text-xs bg-green-500" data-testid={`status-matched-${pod.id}`}>
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Matchad
                            </Badge>
                          ) : pod.match_status === "PRODUCT_MISMATCH" ? (
                            <Badge className="text-xs bg-amber-500" data-testid={`status-mismatch-${pod.id}`}>
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Produktavvikelse
                            </Badge>
                          ) : pod.match_status === "NO_ORDER_MATCH" ? (
                            <Badge className="text-xs bg-red-500" data-testid={`status-no-match-${pod.id}`}>
                              <XCircle className="h-3 w-3 mr-1" />
                              Ingen match
                            </Badge>
                          ) : pod.matched_deliveries > 0 ? (
                            <Badge className="text-xs bg-green-500">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Matchad
                            </Badge>
                          ) : null}
                          {pod.has_document && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewDocument(pod)}
                              disabled={loadingDocument}
                              data-testid={`view-doc-${pod.id}`}
                            >
                              {loadingDocument ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ImageIcon className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditPod(pod)}
                            data-testid={`edit-pod-${pod.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmPod(pod)}
                            data-testid={`delete-pod-${pod.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Pagination controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t mt-4">
                        <span className="text-sm text-muted-foreground">
                          Sida {currentPage} av {totalPages}
                        </span>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoadingHistory}
                            data-testid="prev-page-btn"
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Föregående
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoadingHistory}
                            data-testid="next-page-btn"
                          >
                            Nästa
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      
      {/* Delete confirmation */}
      <AlertDialog open={!!deleteConfirmPod} onOpenChange={() => setDeleteConfirmPod(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ta bort leveransbevis?</AlertDialogTitle>
            <AlertDialogDescription>
              Är du säker på att du vill ta bort leveransbeviset för order{" "}
              <span className="font-mono font-bold">{deleteConfirmPod?.order_number}</span>?
              Detta kan inte ångras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePod(deleteConfirmPod)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ta bort
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* View document dialog */}
      <AlertDialog open={!!viewingDocument} onOpenChange={closeDocumentViewer}>
        <AlertDialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Dokument - Order {viewingDocument?.order_number}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {viewingDocument?.customer_name || "Okänd kund"} • {formatDate(viewingDocument?.created_at)}
              {viewingDocument?.created_by && (
                <span className="ml-2">• Uppladdad av: {viewingDocument.created_by}</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            {viewingDocument?.document_image && (
              <img 
                src={`data:image/jpeg;base64,${viewingDocument.document_image}`}
                alt="Skannat dokument"
                className="w-full rounded-lg"
              />
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Stäng</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Edit POD dialog */}
      <Dialog open={!!editingPod} onOpenChange={(open) => {
        if (!open) {
          setEditingPod(null);
          setEditingPodProducts([]);
          setEditingOrderNumber("");
          setEditingCustomerName("");
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Redigera leveransbevis
            </DialogTitle>
            <DialogDescription>
              Skapad: {formatDate(editingPod?.created_at)}
              {editingPod?.created_by && (
                <span className="ml-2">• Av: {editingPod.created_by}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-auto max-h-[60vh] space-y-4">
            {/* Order number and customer name fields */}
            <Card className="border-primary/20">
              <CardContent className="p-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Ordernummer</Label>
                    <Input
                      value={editingOrderNumber}
                      onChange={(e) => setEditingOrderNumber(e.target.value)}
                      placeholder="Ordernummer / Referens"
                      className="font-mono"
                      data-testid="edit-order-number"
                    />
                    <p className="text-xs text-muted-foreground">
                      Ändra för att matcha med annan leverans
                    </p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Kundnamn</Label>
                    <Input
                      value={editingCustomerName}
                      onChange={(e) => setEditingCustomerName(e.target.value)}
                      placeholder="Kundnamn"
                      data-testid="edit-customer-name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t">
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Frakt (kr)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingFreight}
                      onChange={(e) => setEditingFreight(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="font-mono"
                      data-testid="edit-freight"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Totalt belopp</Label>
                    <div className="h-9 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                      {(editingPodProducts.reduce((sum, p) => sum + (p.salePrice || 0) * (p.quantity || 1), 0) + (editingFreight || 0)).toLocaleString('sv-SE')} kr
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Products */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Produkter</Label>
              {editingPodProducts.map((product, index) => (
                <Card key={index} className={`border ${(product.matched || (product.ean && product.ean.length >= 8)) ? 'border-green-500 bg-green-500/5' : 'border-amber-500 bg-amber-500/5'}`}>
                  <CardContent className="p-3">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      {/* Artikelnummer */}
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Art.nr</Label>
                        <Input
                          value={product.artikelnummer || ""}
                          onChange={(e) => updateEditingProduct(index, "artikelnummer", e.target.value)}
                          placeholder="Artikelnummer"
                          className="text-sm font-mono"
                        />
                      </div>
                      
                      {/* EAN */}
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">EAN</Label>
                        <Input
                          value={product.ean || ""}
                          onChange={(e) => updateEditingProduct(index, "ean", e.target.value)}
                          placeholder="EAN-nummer"
                          className="text-sm font-mono"
                        />
                      </div>
                      
                      {/* Search button */}
                      <div className="col-span-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-9 w-9"
                          onClick={() => searchProductMatch(index, product.artikelnummer || product.ean)}
                          disabled={searchingProduct === index}
                          title="Sök matchning"
                        >
                          {searchingProduct === index ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Search className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      
                      {/* Quantity */}
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Antal</Label>
                        <Input
                          type="number"
                          min="1"
                          value={product.quantity || 1}
                          onChange={(e) => updateEditingProduct(index, "quantity", parseInt(e.target.value) || 1)}
                          className="text-sm text-center"
                        />
                      </div>
                      
                      {/* Sale Price */}
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Utpris (kr)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={product.salePrice ?? 0}
                          onChange={(e) => updateEditingProduct(index, "salePrice", parseFloat(e.target.value) || 0)}
                          className="text-sm text-right"
                          data-testid={`edit-price-${index}`}
                        />
                      </div>
                    </div>
                    
                    {/* Product name */}
                    {(product.productName || product.produktnamn) && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {product.productName || product.produktnamn}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-2 mt-2">
                      {(product.matched || (product.ean && product.ean.length >= 8)) ? (
                        <Badge className="bg-green-500 text-white text-xs">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Matchad i systemet
                        </Badge>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => updateEditingProduct(index, "matched", true)}
                          data-testid={`manual-match-${index}`}
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Manuell matchning
                        </Button>
                      )}
                      
                      {/* Delete product button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7 text-destructive hover:text-destructive ml-auto"
                        onClick={() => removeEditingProduct(index)}
                        data-testid={`delete-product-${index}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Ta bort
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Add product button */}
              <Button
                variant="outline"
                size="sm"
                onClick={addEditingProduct}
                className="w-full"
                data-testid="add-editing-product-btn"
              >
                <Package className="h-4 w-4 mr-2" />
                Lägg till produkt
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingPod(null)}>
              Avbryt
            </Button>
            <Button onClick={saveEditedPod} disabled={savingPodEdit}>
              {savingPodEdit ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sparar...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Spara ändringar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
